import type { List, Site } from '@microsoft/microsoft-graph-types'

export type SharePointListener = () => void

/** What the two Graph calls behind a drive id resolve to, or why they did not. */
export type SharePointResolution = {
    site?: Site
    list?: List
    webUrl?: string
    errorMessage?: string
}

/**
 * Session cache for the site and list behind a host's drive id.
 *
 * `useSharePoint` is called from eleven places - the main menu, the global context, both file headers, the document
 * overviews and four dialogs - and every one of them made its own pair of Graph calls, with no cache and a
 * dependency on tokensRevision that ran them all again on every token refresh. One tenant whose drive id points at
 * a site Graph no longer knows produced 147 of each call in three sessions, every one of them a 404 the user got
 * to see.
 *
 * The site and list behind a drive id do not change during a session, so one resolution per drive id is enough.
 * Callers that arrive while a resolution is in flight wait for that same request rather than starting another.
 */
export type SharePointStore = {
    /** Bumped on every write, so `useSyncExternalStore` has a stable, primitive snapshot to compare. */
    getRevision(): number
    get(driveId: string | undefined): SharePointResolution | undefined
    /** Resolves the drive id once, sharing the request with anyone who asks for it in the meantime. */
    resolve(driveId: string | undefined, load: () => Promise<SharePointResolution>): Promise<SharePointResolution | undefined>
    /**
     * Drops the resolutions that failed, so they are retried once. Successful ones are kept: a site does not stop
     * being that site because the access token was refreshed.
     */
    invalidateFailed(): void
    subscribe(listener: SharePointListener): () => void
}

export function createSharePointStore(): SharePointStore {
    const resolutions = new Map<string, SharePointResolution>()
    const inFlight = new Map<string, Promise<SharePointResolution | undefined>>()
    const listeners = new Set<SharePointListener>()
    let revision = 0

    const notify = () => {
        revision += 1
        listeners.forEach(listener => listener())
    }

    return {
        getRevision(): number {
            return revision
        },

        get(driveId: string | undefined): SharePointResolution | undefined {
            return driveId ? resolutions.get(driveId) : undefined
        },

        resolve(driveId: string | undefined, load: () => Promise<SharePointResolution>): Promise<SharePointResolution | undefined> {
            if (!driveId) {
                return Promise.resolve(undefined)
            }

            const resolved = resolutions.get(driveId)
            if (resolved) {
                return Promise.resolve(resolved)
            }

            const running = inFlight.get(driveId)
            if (running) {
                return running
            }

            const request = load()
                .then(resolution => {
                    resolutions.set(driveId, resolution)
                    return resolution
                })
                .finally(() => {
                    inFlight.delete(driveId)
                    notify()
                })

            inFlight.set(driveId, request)
            return request
        },

        invalidateFailed() {
            const failed = [...resolutions.entries()].filter(([, resolution]) => resolution.errorMessage).map(([driveId]) => driveId)
            if (!failed.length) {
                return
            }

            failed.forEach(driveId => resolutions.delete(driveId))
            notify()
        },

        subscribe(listener: SharePointListener): () => void {
            listeners.add(listener)
            return () => {
                listeners.delete(listener)
            }
        },
    }
}
